/**
 * Typewriter Engine - Halftone Theme
 *
 * Shared reveal logic for the typewriter-heading section and the
 * typewriter-text product block. Scans [data-typewriter] containers whose
 * [data-tw-line] children each hold a .tw-text span with the real text.
 *
 * The full text is server-rendered inside .tw-text, so crawlers, no-JS
 * visitors, and reduced-motion users always get the complete text. This script
 * only reveals it progressively; it is purely a visual layer.
 */
(function () {
  // Typing cadence per character (ms). Medium keeps the deliberate terminal feel.
  const SPEEDS = { slow: 125, medium: 90, fast: 55 };
  // Extra beat held before a space so each word lands before the next.
  const WORD_PAUSE = 260;

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function motionDisabled() {
    if (prefersReducedMotion()) return true;
    // Animate in the theme editor even though design mode adds animations-disabled
    if (window.Shopify && Shopify.designMode) return false;
    return document.body.classList.contains('animations-disabled');
  }

  function makeCursor() {
    const cursor = document.createElement('span');
    cursor.className = 'tw-cursor';
    cursor.setAttribute('aria-hidden', 'true');
    return cursor;
  }

  function initTypewriter(root) {
    // Mark init on the element itself: in the editor the load event can fire
    // more than once; a DOM-level guard makes them converge on one init.
    if (root.twInitialized) return;
    root.twInitialized = true;

    const lineEls = Array.from(root.querySelectorAll('[data-tw-line]'));
    if (!lineEls.length) return;

    // Capture each line's real text and expose it as the accessible name,
    // so screen readers always announce the complete text.
    const lines = lineEls
      .map(function (line) {
        const textEl = line.querySelector('.tw-text');
        if (!textEl) return null;
        const text = textEl.textContent;
        line.setAttribute('aria-label', text.trim());
        return { textEl: textEl, text: text, chars: [] };
      })
      .filter(Boolean);

    if (!lines.length) return;

    // Motion disabled: leave the full text as-is, drop a static cursor at the
    // end of the last line. No typing, no blink.
    if (motionDisabled()) {
      lines[lines.length - 1].textEl.appendChild(makeCursor());
      return;
    }

    // Split each line into per-character spans. They all stay in the flow and
    // only become visible as they are typed, so each line wraps once from the
    // complete text — no reflow, no word hopping between lines, no layout
    // shift. See .tw-char in assets/typewriter.css.
    lines.forEach(function (line) {
      line.textEl.textContent = '';
      const frag = document.createDocumentFragment();
      Array.from(line.text).forEach(function (ch) {
        const span = document.createElement('span');
        span.className = 'tw-char';
        span.textContent = ch;
        frag.appendChild(span);
        line.chars.push(span);
      });
      line.textEl.appendChild(frag);
    });

    const typeSpeed = SPEEDS[root.getAttribute('data-speed')] || SPEEDS.medium;
    const cursor = makeCursor();
    const timers = [];
    let started = false;

    // Move the cursor to the caret: before the next character still to be
    // typed, or to the end of the line once there are none left.
    function moveCursor(line, next) {
      if (next) line.textEl.insertBefore(cursor, next);
      else line.textEl.appendChild(cursor);
    }

    // Idle state before typing: a blinking cursor at the start of line one.
    cursor.classList.add('is-blinking');
    moveCursor(lines[0], lines[0].chars[0]);

    function typeLine(index) {
      const line = lines[index];
      if (!line) {
        // All lines done: cursor stays at the end, blinking.
        cursor.classList.add('is-blinking');
        return;
      }

      cursor.classList.remove('is-blinking');
      moveCursor(line, line.chars[0]);

      let i = 0;
      function step() {
        if (i < line.chars.length) {
          line.chars[i].classList.add('is-typed');
          i += 1;
          const next = line.chars[i];
          moveCursor(line, next);
          // Linger a beat before a space so each word lands before the next.
          const delay = next && /\s/.test(next.textContent) ? typeSpeed + WORD_PAUSE : typeSpeed;
          timers.push(setTimeout(step, delay));
        } else {
          typeLine(index + 1);
        }
      }
      step();
    }

    function start() {
      if (started) return;
      started = true;
      typeLine(0);
    }

    let observer = null;
    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              start();
              if (observer) {
                observer.disconnect();
                observer = null;
              }
            }
          });
        },
        { threshold: 0.25 }
      );
      observer.observe(root);
    } else {
      start();
    }

    // No resize handling needed: every character keeps its box at all times,
    // so the browser re-wraps the full text on resize exactly as it would if
    // nothing were being typed.

    root._twCleanup = function () {
      timers.forEach(clearTimeout);
      if (observer) {
        observer.disconnect();
        observer = null;
      }
      root.twInitialized = false;
    };
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-typewriter]').forEach(initTypewriter);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  // Theme editor: (re)initialise and clean up as sections load/unload.
  if (window.Shopify && Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (e) {
      e.target.querySelectorAll('[data-typewriter]').forEach(initTypewriter);
    });
    document.addEventListener('shopify:section:unload', function (e) {
      e.target.querySelectorAll('[data-typewriter]').forEach(function (el) {
        if (typeof el._twCleanup === 'function') el._twCleanup();
      });
    });
  }
})();
